import React, { useEffect, useState } from "react";
import MainLayout from "@/pages/auth/Login/components/MainLayout";
import { ReactComponent as LayoutImage } from "@/assets/images/UserLogin.svg"
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  styled,
  TextField,
  Typography,
  Select, MenuItem
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {object, string, TypeOf, z} from "zod";
import WebURL from "@/urls";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSnackbar } from "notistack";
import UserService from "@/services/auth/UserService";
import { LoadingButton } from "@mui/lab";
import CustomLink from "@/pages/auth/Login/components/CustomLink";
import LogoView from "@/pages/auth/Login/components/LogoView";
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";
import ControlledPasswordFiled from "@/components/ngteco-hook-form/ControlledPasswordFiled";
import SignUpUserAgreement from "@/pages/auth/Login/components/SignUpUserAgreement";
import SignUpPrivacyPolicy from "@/pages/auth/Login/components/SignUpPrivacyPolicy";
import ControlAutoComplete from "@/components/hook-form/ControlAutoComplete";
import Button from '@mui/material/Button';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import NGUserAgreement from "@/components/ngteco-form/NGUserAgreement"
import NGPrivacyPolicy from "@/components/ngteco-form/NGPrivacyPolicy"
import CountryService from "@/services/cis/CountryService";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";


const SignUp: React.FC = () => {
  const native = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const re_math =  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[.#?!@$%^&*-]).{6,}$/;

  const signUpSchema = object({
    first_name: z.string().min(1, t("common.This field is required!")).max(120, t("common.Max length of name is 120")),
    last_name: z.string().min(1, t("common.This field is required!")).max(120, t("common.Max length of name is 120")),
    email: z.string().min(1, t("common.This field is required!"))
      .max(50, t("common.The length of field cannot exceed digits", {number: 50}))
      .email(t("common.Invalid email format")),
    password: z.string().min(1, t("common.This field is required!")),
    confirm_password: z.string().min(1, t("common.This field is required!")),
    country_dict: z.object({
      id: z.string(),
      name: z.string(),
    }, { required_error: t("common.This field is required!"), invalid_type_error: t("common.This field is required!")} ),
    state: z.string().optional(),
    country: z.string().optional(),
  }).refine((data) => data.password === data.confirm_password, {
    message: t("organ.Confirm Password don't match"),
    path: ["confirm_password"]
  }).refine((data) => re_math.test(data.password), {
    message: t("organ.Password verification policy"),
    path: ["password"]
  })

  type signUpInput = TypeOf<typeof signUpSchema>;

  const signUpForm = useForm<signUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      confirm_password: "",
      country: '',
      country_dict: {},
      state: '',
    }
  })
  const [policy, setPolicy] = useState<string>("main");
  const { register, handleSubmit, getValues, formState: { errors } } = signUpForm
  const [userAgreement, setUserAgreement] = useState<boolean>(false);
  const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(false);
  const [agreementOpen, setAgreementOpen] = React.useState(false);
  const [privacyOpen, setPrivacyOpen] = React.useState(false);
  const [country, setCountry] = React.useState<string>('');
  const [stateValue, setStateValue] = useState<string>('')
  const [stateDict, setStateDict] = useState([])


  const { enqueueSnackbar } = useSnackbar();
  const handleClickOpen = (type: any) => () => {
    if (type === "Agreement") {
      setAgreementOpen(true);
    } else {
      setPrivacyOpen(true)
    }
  };

  const handleClose = (type: any) => {
    if (type === "Agreement") {
      setAgreementOpen(false);
    } else {
      setPrivacyOpen(false)
    }
  };

  interface DictOption {
    id: string;
    name: string;
  }

  const [countryList, setCountryList] = useState<DictOption[]>([])

  useEffect(() => {
    CountryService.getCountryList()
      .then((result) => {
        const countries = Array.isArray(result?.data?.data?.countries)
          ? result.data.data.countries
          : [];

        const options = countries.map((country: any, index: number) => {
          return {
            id: country.iso31661Cd2,
            name: country.name,
          };
        });

        setCountryList(options);
      })
      .catch((error) => {
        console.error('获取国家列表失败：', error);
        setCountryList([]);
      });
}, []);
  useEffect(() => {
    setStateValue('')
    if (!country) {
      setStateDict([])
    } else if (country) {
      CountryService.getCountryState(country).then((result) => {
        const compliance_country = result.data.data?.compliance_country
        if (compliance_country) {
          const state_data = result.data.data?.state
          setStateDict(state_data)
        }
      })
    }
  }, [country])

  const onSignUp = async () => {
    const data = getValues()
    const email = data.email
    if (email.length > 50) {
      enqueueSnackbar(t("common.The length of field cannot exceed digits", {number: 50}), {
        variant: "warning",
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      })
      return
    }
    if (Object.keys(stateDict).length != 0 && !stateValue) {
      enqueueSnackbar(t("common.The State field is required"), {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      })
      return
    }

    if (!userAgreement || !privacyPolicy) {
      enqueueSnackbar(t("organ.Please read user agreement and privacy policy before sign up"), {
        variant: "warning",
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      })
      return
    }
    try {
      setLoading(true)
      const { status, res_data } = await UserService.signUp(data);
      enqueueSnackbar(t("common.success"), {
        variant: "success",
        autoHideDuration: 1500,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      })
      native(WebURL.AUTH_USER_LOGIN);
    } finally {
      setLoading(false)
    }
  };
  return (
    <MainLayout image={<LayoutImage />}>
      <Grid sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #CAD0D9",
        marginTop: "3vh",
        marginBottom: "2vh",
        minHeight: "780px",
        width: '34vw',
        borderRadius: "12px",
        backgroundColor: "#ffffff"
      }}>
        <LogoView />
        {policy === "main" ?
          <Grid component={"form"} style={{ width: '28vw' }}>
            <Typography variant="h5" sx={{marginTop: '12x'}}>{t("signUp.Ready to sign up?")}</Typography>
            <Stack spacing={1} sx={{ overflow: "auto", marginTop: '15px' }}>
              <ControlledTextFiled
                fullWidth required hookForm={signUpForm}
                name={'first_name'}
                label={t("signUp.First Name")}
              />
              <ControlledTextFiled
                fullWidth required hookForm={signUpForm}
                name={'last_name'}
                label={t("signUp.Last Name")}
              />
              <ControlledTextFiled
                fullWidth required hookForm={signUpForm}
                name={'email'} label={t("signUp.Enter the email")}
              />
              <ControlAutoComplete
                label={t("signUp.Country or Region")}
                name={"country_dict"}
                required
                form={signUpForm}
                options={countryList}
                isOptionEqualToValue={(option: DictOption, value: DictOption | string) => {
                  const targetValue = typeof value === 'object' ? value.name : value;
                  return option.name === targetValue;
                }}
                onChange={(e) => {
                  setCountry(e?.id)
                  signUpForm.setValue("country", e?.name)
                }}
              />
              <Stack sx={{lineHeight: "5vh"}}>
                <NGInputLabel label={t("signUp.State")} required={Object.keys(stateDict).length != 0}/>
                <Select
                  id="select_state"
                  fullWidth
                  // required={true}
                  value={stateValue}
                  onChange={(event) => {
                    setStateValue(event.target.value)
                    signUpForm.setValue("state", event.target.value)
                  }}
                >
                  {Object.entries(stateDict).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              {/*<ControlAutoComplete*/}
              {/*  label={t("signUp.State")}*/}
              {/*  name={"state"}*/}
              {/*  required*/}
              {/*  form={signUpForm}*/}
              {/*  options={stateList}*/}
              {/*  isOptionEqualToValue={(option: DictOption, value: DictOption | string) => {*/}
              {/*    const targetValue = typeof value === 'object' ? value.id : value;*/}
              {/*    return option.id === targetValue;*/}
              {/*  }}*/}
              {/*/>*/}
              <ControlledPasswordFiled
                fullWidth required hookForm={signUpForm}
                name={'password'}
                label={t("signUp.Password")}
              />
              <ControlledPasswordFiled
                fullWidth required hookForm={signUpForm}
                name={'confirm_password'}
                label={t("signUp.Confirm Password")}
              />
            </Stack>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
              <FormControlLabel control={<Checkbox checked={userAgreement}
                                                   onChange={(event) => {
                                                     setUserAgreement(event.target.checked)
                                                   }}/>} label={
                <Box sx={{ display: "flex", justifyContent: "center"}}>
                  <Typography sx={{marginTop:"7px", color: '#3D3D3D', fontSize: '14px'}}>{t("signUp.I have read and agree to the")}</Typography>
                  <Button sx={{color: '#2BA9DE'}} onClick={handleClickOpen("Agreement")}>{t("common.User Agreement")}</Button>
                  <Dialog
                    open={agreementOpen}
                    onClose={()=>{handleClose("Agreement")}}
                    scroll={"paper"}
                    maxWidth={"md"}
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                  >
                    <DialogTitle id="scroll-dialog-title">User Agreement</DialogTitle>
                    <DialogContent dividers={true}>
                      <DialogContentText
                        id="scroll-dialog-User Agreement"
                        tabIndex={-1}
                      >
                        <NGUserAgreement/>
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => {handleClose("Agreement"); setUserAgreement(false)}}>{t("signUp.Not agree and quit")}</Button>
                      <Button variant="contained" onClick={() => {handleClose("Agreement"); setUserAgreement(true);}}>{t("signUp.Read and agree")}</Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              } />
              <FormControlLabel control={<Checkbox checked={privacyPolicy}
                                                   onChange={(event) => {
                                                     setPrivacyPolicy(event.target.checked)
                                                   }}/>} label={
                <Box sx={{ display: "flex" }}>
                  <Typography sx={{marginTop:"7px", color: '#3D3D3D', fontSize: '14px'}}>{t("signUp.I have read and agree to the")}</Typography>
                  <Button sx={{color: '#2BA9DE'}} onClick={handleClickOpen("Privacy")}>{t("signUp.Privacy policy")}</Button>
                  <Dialog
                    open={privacyOpen}
                    onClose={()=>{handleClose("Privacy")}}
                    scroll={"paper"}
                    maxWidth={"md"}
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                  >
                    <DialogTitle id="scroll-dialog-title">Personal Data Protection and Privacy Policy</DialogTitle>
                    <DialogContent dividers={true}>
                      <DialogContentText
                        id="scroll-dialog-Privacy policy"
                        tabIndex={-1}
                      >
                        <NGPrivacyPolicy/>
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => {handleClose("Privacy"); setPrivacyPolicy(false)}}>{t("signUp.Not agree and quit")}</Button>
                      <Button variant="contained" onClick={() => {handleClose("Privacy"); setPrivacyPolicy(true);}}>{t("signUp.Read and agree")}</Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              } />
            </Box>
            <LoadingButton
              fullWidth={true}
              variant={"contained"}
              loading={loading}
              // loadingPosition={"start"}
              sx={{ margin: '8px 0', textTransform: "none",backgroundColor: '#2BA9DE' }}
              onClick={signUpForm.handleSubmit(onSignUp)}
            >
              {t("signUp.Sign up")}
            </LoadingButton>
          </Grid>
          : policy === "privatePolicy" ?
            <SignUpPrivacyPolicy setPolicy={setPolicy} setPrivacyPolicy={setPrivacyPolicy} />
            : <SignUpUserAgreement setPolicy={setPolicy} setUserAgreement={setUserAgreement} />
        }
        <Grid sx={{
          display: "inline-flex",
          justifyContent: "center",
          paddingBottom: 1,
          bottom: 0,
          position: "position",
          textAlign: 'center',
          width: '28vw',
        }}>
          <Typography sx={{ fontSize: "14px" }}>{t("login.Already have an account?")}</Typography>
          <CustomLink sx={{color: '#2BA9DE'}}
                      label={t("login.login")}
                      onClick={() => native(WebURL.AUTH_USER_LOGIN)}
          />
        </Grid>
      </Grid>
    </MainLayout>
  )
}
export default SignUp
